import React, { useEffect, useRef, useState } from 'react';
import { Building2, Calendar } from 'lucide-react';
import { experiences } from '../data/mock';
import { Badge } from './ui/badge';

const Experience = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="py-32 bg-[#0a0a0b] relative overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[150px]" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="overflow-hidden">
            <p
              className={`text-indigo-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Career Journey
            </p>
          </div>
          <div className="overflow-hidden">
            <h2
              className={`text-4xl md:text-5xl font-light text-white transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Experience
            </h2>
          </div>
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-0 md:left-1/2 transform md:-translate-x-px h-full w-0.5 bg-gradient-to-b from-indigo-500/50 via-purple-500/50 to-transparent" />

          {experiences.map((exp, index) => (
            <div
              key={exp.id}
              className={`relative mb-12 md:mb-20 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              <div className={`flex flex-col md:flex-row items-start ${
                index % 2 === 0 ? 'md:flex-row-reverse' : ''
              }`}>
                {/* Timeline Dot */}
                <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 w-4 h-4 bg-indigo-500 rounded-full border-4 border-[#0a0a0b] shadow-[0_0_20px_rgba(99,102,241,0.5)] z-10" />
                
                {/* Content Card */}
                <div className={`ml-8 md:ml-0 md:w-1/2 ${
                  index % 2 === 0 ? 'md:pl-12' : 'md:pr-12'
                }`}>
                  <div className="group bg-white/[0.03] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.05] hover:border-indigo-500/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(99,102,241,0.1)]">
                    {/* Company Badge */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Building2 size={20} className="text-white" />
                      </div>
                      <div>
                        <h3
                          className="text-xl font-medium text-white group-hover:text-indigo-400 transition-colors duration-500"
                          style={{ fontFamily: 'Figtree, sans-serif' }}
                        >
                          {exp.company}
                        </h3>
                        <p
                          className="text-gray-500 text-sm"
                          style={{ fontFamily: 'Figtree, sans-serif' }}
                        >
                          {exp.role}
                        </p>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                      <Calendar size={14} />
                      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{exp.duration}</span>
                      {exp.type === 'current' && (
                        <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30">
                          Current
                        </Badge>
                      )}
                    </div>

                    {/* Description */}
                    <p
                      className="text-gray-400 mb-6 leading-relaxed"
                      style={{ fontFamily: 'Figtree, sans-serif' }}
                    >
                      {exp.description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 text-xs bg-white/5 text-gray-400 rounded-full border border-white/10 hover:border-indigo-500/50 hover:text-indigo-400 transition-all duration-300"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
